import React from 'react';
import { Modal } from 'antd';
import { IBookingLimitPreviewItem } from 'types/bookingLimit';
import s from './s.module.less';

interface ConfirmSaveProps {
    open: boolean;
    isPractice: boolean;
    /** 新上限 */
    limitCount: number;
    /** 来自 save-preview 的 willPause —— 不要在前端自己算谁会被暂停 */
    willPause: IBookingLimitPreviewItem[];
    /** providerId → 名字。practice 视图用，solo 用不到 */
    nameOf?: (providerId: number) => string;
    resetAt: string;
    loading: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

/**
 * 调低上限会立刻把已超标的人从搜索里摘掉，这是不可逆且用户可见的后果，
 * 所以保存前必须确认，并点名列出受影响的人。
 */
export const ConfirmSaveModal: React.FC<ConfirmSaveProps> = ({
    open,
    isPractice,
    limitCount,
    willPause,
    nameOf,
    resetAt,
    loading,
    onCancel,
    onConfirm,
}) => {
    const n = willPause.length;
    const names = nameOf ? willPause.map((p) => nameOf(p.providerId)).filter(Boolean).join(', ') : '';
    const used = willPause[0]?.usedCount ?? 0;

    return (
        <Modal
            open={open}
            title={isPractice ? 'Save and hide these profiles now?' : 'Save and hide your profile now?'}
            okText="Save limit"
            cancelText="Cancel"
            confirmLoading={loading}
            onCancel={onCancel}
            onOk={onConfirm}
            width={560}
            className={s.modalShell}
        >
            {isPractice ? (
                <p>
                    <b>
                        {n} provider{n === 1 ? '' : 's'}
                    </b>{' '}
                    {n === 1 ? 'has' : 'have'} already reached {limitCount} new patients this week
                    {names ? `: ${names}` : ''}. {n === 1 ? 'Their profile is' : 'Their profiles are'}{' '}
                    hidden from marketplace search as soon as you save and show again {resetAt}.
                </p>
            ) : (
                <p>
                    <b>{used} new patients</b> have already booked you this week, which is{' '}
                    {used === limitCount ? 'exactly' : 'more than'} the new limit of <b>{limitCount}</b>.
                    Your profile is hidden from marketplace search as soon as you save and shows again{' '}
                    {resetAt}.
                </p>
            )}
            <p>Appointments already booked stay as they are.</p>
        </Modal>
    );
};

interface ConfirmTurnOffProps {
    open: boolean;
    isPractice: boolean;
    loading: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

/** 关闭开关也要确认：它把 practice 放回不限量，每一单都会产生 lead fee。 */
export const ConfirmTurnOffModal: React.FC<ConfirmTurnOffProps> = ({
    open,
    isPractice,
    loading,
    onCancel,
    onConfirm,
}) => (
    <Modal
        open={open}
        title={isPractice ? 'Turn off the practice limit?' : 'Turn off your weekly limit?'}
        okText="Turn off"
        cancelText="Cancel"
        confirmLoading={loading}
        onCancel={onCancel}
        onOk={onConfirm}
        width={560}
        className={s.modalShell}
    >
        <p>
            {isPractice ? 'The practice goes back to ' : 'You go back to '}
            <b>Unlimited</b>: any number of new patients can book{' '}
            {isPractice ? 'each provider ' : 'you '}
            through the Klarity Health marketplace, and the lead fee applies to every booking. Your
            number stays saved, so you can turn the limit back on at any time.
        </p>
    </Modal>
);
